import { specialDocumentRequirementsEnabled } from '@autocontrol/api-contracts';
import type {
  JsonValue,
  ResolvedContractorDocumentRequirementDto,
  ResolvedContractorFieldDto,
  ResolvedContractorFormSchemaDto,
} from './api';
import {
  documentIdentityKey,
  isContractorEditable,
  isDocumentEditComplete,
  isMetadataFieldRequired,
  readEnumOptions,
  resolveFieldGroups,
  type DocumentEditState,
  type FieldEditState,
} from './contractor-questionnaire-model';

export interface DynamicQuestionnaireProps {
  readonly schema: ResolvedContractorFormSchemaDto;
  readonly documents: readonly ResolvedContractorDocumentRequirementDto[];
  readonly fieldEdits: FieldEditState;
  readonly documentEdits: DocumentEditState;
  readonly fieldErrors: ReadonlyMap<string, string>;
  readonly documentErrors: ReadonlyMap<string, string>;
  readonly readOnly: boolean;
  readonly busy: boolean;
  readonly attentionFieldKeys?: ReadonlySet<string>;
  readonly attentionDocumentKeys?: ReadonlySet<string>;
  /** When false, skip field groups (documents-only). Default true. */
  readonly showFields?: boolean;
  /**
   * When false, skip documents section. Default follows
   * specialDocumentRequirementsEnabled() (currently soft-disabled).
   */
  readonly showDocuments?: boolean;
  /** Documents section heading; null omits the heading (parent card supplies it). */
  readonly documentsHeading?: string | null;
  readonly onFieldChange: (fieldKey: string, value: JsonValue | undefined) => void;
  readonly onDocumentPresentChange: (documentKey: string, present: boolean) => void;
  readonly onDocumentMetadataChange: (
    documentKey: string,
    fieldCode: string,
    value: JsonValue | undefined,
  ) => void;
}

/**
 * Production contractor questionnaire renderer.
 * Visibility and controls are driven solely by the server resolved schema —
 * no PS/TS/template_code branching.
 */
export function DynamicQuestionnaire(props: DynamicQuestionnaireProps) {
  const showFields = props.showFields !== false;
  const showDocuments =
    props.showDocuments ?? specialDocumentRequirementsEnabled();
  const groups = resolveFieldGroups(props.schema);
  const applicableFields = props.schema.fields.filter((field) => field.applicable);
  const applicableDocuments = props.documents.filter((doc) => doc.applicable);

  if (
    (showFields ? applicableFields.length === 0 : true) &&
    (showDocuments ? applicableDocuments.length === 0 : true)
  ) {
    if (!showFields || !showDocuments) {
      return null;
    }
    return (
      <section className="questionnaire-empty" aria-live="polite">
        <p role="status">
          Для выбранной конфигурации нет применимых полей и документов подрядчика.
        </p>
      </section>
    );
  }

  return (
    <div className="questionnaire">
      {showFields
        ? groups.map((group) => {
            const visible = group.fields.filter((field) => field.applicable);
            if (visible.length === 0) {
              return null;
            }
            return (
              <section
                key={group.id}
                className="questionnaire-section"
                aria-labelledby={`group-${group.id}`}
              >
                <h3 id={`group-${group.id}`} className="questionnaire-group-title">
                  {group.title}
                </h3>
                {visible.map((field) => (
                  <DynamicFieldControl
                    key={field.field_key}
                    field={field}
                    value={props.fieldEdits[field.field_key]}
                    error={props.fieldErrors.get(field.field_key)}
                    attention={props.attentionFieldKeys?.has(field.field_key) === true}
                    readOnly={props.readOnly}
                    busy={props.busy}
                    onChange={(value) => {
                      props.onFieldChange(field.field_key, value);
                    }}
                  />
                ))}
              </section>
            );
          })
        : null}

      {showDocuments && applicableDocuments.length > 0 ? (
        <section
          className="questionnaire-section"
          aria-labelledby={props.documentsHeading === null ? undefined : 'documents-heading'}
        >
          {props.documentsHeading === null ? null : (
            <h2 id="documents-heading" className="questionnaire-section-title">
              {props.documentsHeading ?? 'Документы'}
            </h2>
          )}
          {applicableDocuments.map((doc) => {
            const key = documentIdentityKey(doc.document_type_id, doc.subject_binding);
            const edit = props.documentEdits[key] ?? { present: false, metadata: {} };
            return (
              <DocumentRequirementControl
                key={key}
                document={doc}
                documentKey={key}
                present={edit.present}
                metadata={edit.metadata}
                error={props.documentErrors.get(key)}
                attention={props.attentionDocumentKeys?.has(key) === true}
                readOnly={props.readOnly}
                busy={props.busy}
                onPresentChange={(present) => {
                  props.onDocumentPresentChange(key, present);
                }}
                onMetadataChange={(fieldCode, value) => {
                  props.onDocumentMetadataChange(key, fieldCode, value);
                }}
              />
            );
          })}
        </section>
      ) : null}
    </div>
  );
}

function DynamicFieldControl(props: {
  readonly field: ResolvedContractorFieldDto;
  readonly value: JsonValue | undefined;
  readonly error?: string;
  readonly attention: boolean;
  readonly readOnly: boolean;
  readonly busy: boolean;
  readonly onChange: (value: JsonValue | undefined) => void;
}) {
  const editable = isContractorEditable(props.field) && !props.readOnly;
  const disabled = !editable || props.busy;
  const id = `field-${props.field.field_key}`;
  const ownerHint =
    props.field.owner_code === 'CONTRACTOR'
      ? null
      : props.field.owner_code === 'INSPECTOR'
        ? 'Заполняет инспектор'
        : 'Системное поле';
  const displayValue = formatDisplayValue(props.value);
  const className = [
    'questionnaire-field',
    props.attention ? 'questionnaire-field-attention' : '',
    props.field.owner_code !== 'CONTRACTOR' ? 'questionnaire-field-readonly' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={className}>
      <label htmlFor={id}>
        {props.field.label}
        {props.field.required && props.field.applicable ? <span aria-hidden="true"> *</span> : null}
        {ownerHint !== null ? <span className="field-owner-hint"> ({ownerHint})</span> : null}
        <FieldInput
          id={id}
          field={props.field}
          value={displayValue}
          rawValue={props.value}
          disabled={disabled}
          required={props.field.required && props.field.applicable && editable}
          onChange={props.onChange}
        />
      </label>
      {props.error !== undefined ? (
        <p role="alert" className="field-error">
          {props.error}
        </p>
      ) : null}
    </div>
  );
}

function FieldInput(props: {
  readonly id: string;
  readonly field: ResolvedContractorFieldDto;
  readonly value: string;
  readonly rawValue: JsonValue | undefined;
  readonly disabled: boolean;
  readonly required: boolean;
  readonly onChange: (value: JsonValue | undefined) => void;
}) {
  const common = {
    id: props.id,
    name: props.field.field_key,
    disabled: props.disabled,
    'aria-required': props.required || undefined,
    value: props.value,
  };

  switch (props.field.value_type) {
    case 'TEXT':
      return (
        <textarea
          {...common}
          rows={3}
          onChange={(event) => {
            props.onChange(event.target.value);
          }}
        />
      );
    case 'NUMBER':
      return (
        <input
          {...common}
          type="number"
          onChange={(event) => {
            const raw = event.target.value;
            if (raw === '') {
              props.onChange(undefined);
              return;
            }
            const parsed = Number(raw);
            props.onChange(Number.isFinite(parsed) ? parsed : raw);
          }}
        />
      );
    case 'DATE':
      return (
        <input
          {...common}
          type="date"
          onChange={(event) => {
            props.onChange(event.target.value === '' ? undefined : event.target.value);
          }}
        />
      );
    case 'DATETIME':
      return (
        <input
          {...common}
          type="datetime-local"
          onChange={(event) => {
            props.onChange(event.target.value === '' ? undefined : event.target.value);
          }}
        />
      );
    case 'BOOLEAN':
      return (
        <input
          id={props.id}
          name={props.field.field_key}
          type="checkbox"
          disabled={props.disabled}
          aria-required={props.required || undefined}
          checked={props.rawValue === true}
          onChange={(event) => {
            props.onChange(event.target.checked);
          }}
        />
      );
    case 'ENUM': {
      const options = readEnumOptions(props.field.validation);
      return (
        <select
          {...common}
          onChange={(event) => {
            props.onChange(event.target.value === '' ? undefined : event.target.value);
          }}
        >
          <option value="">—</option>
          {options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      );
    }
    case 'STRING':
    case 'REF_CONTRACTOR':
    case 'REF_VEHICLE':
    case 'REF_PERSON':
      return (
        <input
          {...common}
          type="text"
          onChange={(event) => {
            props.onChange(event.target.value);
          }}
        />
      );
    default:
      return (
        <div role="alert" className="banner banner-conflict">
          <p>
            Поле «{props.field.label}» имеет неподдерживаемый тип и не может быть заполнено здесь.
          </p>
          <p className="meta">Обратитесь к администратору конфигурации.</p>
        </div>
      );
  }
}

function DocumentRequirementControl(props: {
  readonly document: ResolvedContractorDocumentRequirementDto;
  readonly documentKey: string;
  readonly present: boolean;
  readonly metadata: Record<string, JsonValue>;
  readonly error?: string;
  readonly attention: boolean;
  readonly readOnly: boolean;
  readonly busy: boolean;
  readonly onPresentChange: (present: boolean) => void;
  readonly onMetadataChange: (fieldCode: string, value: JsonValue | undefined) => void;
}) {
  const disabled = props.readOnly || props.busy;
  const className = [
    'document-requirement',
    props.attention ? 'document-requirement-attention' : '',
    props.document.required ? 'document-requirement-required' : '',
  ]
    .filter(Boolean)
    .join(' ');
  const presenceId = `doc-present-${props.documentKey}`;
  const complete = isDocumentEditComplete(props.document, {
    present: props.present,
    metadata: props.metadata,
  });
  const isMetadata = props.document.capture_mode === 'METADATA_PREFILL';
  const requirementLabel = props.document.required ? 'Обязательный' : 'Необязательный';
  const completionLabel = complete ? 'Заполнен' : 'Не заполнен';

  return (
    <fieldset className={className}>
      <legend>
        {props.document.display_label}
        {props.document.required ? <span aria-hidden="true"> *</span> : null}
      </legend>
      <p className="document-requirement-status meta">
        {requirementLabel} · {completionLabel}
      </p>
      {isMetadata ? (
        <p className="meta">Укажите реквизиты документа</p>
      ) : (
        <p className="meta">Подтвердите наличие документа</p>
      )}
      <label htmlFor={presenceId} className="document-presence">
        <input
          id={presenceId}
          type="checkbox"
          checked={props.present}
          disabled={disabled}
          aria-required={props.document.required || undefined}
          onChange={(event) => {
            props.onPresentChange(event.target.checked);
          }}
        />
        {isMetadata
          ? props.present
            ? 'Скрыть реквизиты'
            : props.document.required
              ? 'Заполнить реквизиты (обязательно)'
              : 'Заполнить реквизиты'
          : 'Документ предоставлен'}
      </label>
      {props.present && isMetadata
        ? props.document.metadata_fields
            .slice()
            .sort((left, right) => left.ordinal - right.ordinal)
            .map((meta) => {
              const metaId = `doc-meta-${props.documentKey}-${meta.field_code}`;
              const required = isMetadataFieldRequired(meta);
              const value = formatDisplayValue(props.metadata[meta.field_code]);
              return (
                <label key={meta.field_code} htmlFor={metaId}>
                  {meta.label}
                  {required ? <span aria-hidden="true"> *</span> : null}
                  <MetadataInput
                    id={metaId}
                    valueType={meta.value_type}
                    validation={meta.validation}
                    value={value}
                    disabled={disabled}
                    required={required}
                    onChange={(next) => {
                      props.onMetadataChange(meta.field_code, next);
                    }}
                  />
                </label>
              );
            })
        : null}
      {props.error !== undefined ? (
        <p role="alert" className="field-error">
          {props.error}
        </p>
      ) : null}
    </fieldset>
  );
}

function MetadataInput(props: {
  readonly id: string;
  readonly valueType: string;
  readonly validation: Record<string, JsonValue>;
  readonly value: string;
  readonly disabled: boolean;
  readonly required: boolean;
  readonly onChange: (value: JsonValue | undefined) => void;
}) {
  if (props.valueType === 'TEXT') {
    return (
      <textarea
        id={props.id}
        rows={2}
        value={props.value}
        disabled={props.disabled}
        aria-required={props.required || undefined}
        onChange={(event) => {
          props.onChange(event.target.value);
        }}
      />
    );
  }
  if (props.valueType === 'NUMBER') {
    return (
      <input
        id={props.id}
        type="number"
        value={props.value}
        disabled={props.disabled}
        aria-required={props.required || undefined}
        onChange={(event) => {
          const raw = event.target.value;
          if (raw === '') {
            props.onChange(undefined);
            return;
          }
          const parsed = Number(raw);
          props.onChange(Number.isFinite(parsed) ? parsed : raw);
        }}
      />
    );
  }
  if (props.valueType === 'DATE') {
    return (
      <input
        id={props.id}
        type="date"
        value={props.value}
        disabled={props.disabled}
        aria-required={props.required || undefined}
        onChange={(event) => {
          props.onChange(event.target.value === '' ? undefined : event.target.value);
        }}
      />
    );
  }
  if (props.valueType === 'DATETIME') {
    return (
      <input
        id={props.id}
        type="datetime-local"
        value={props.value}
        disabled={props.disabled}
        aria-required={props.required || undefined}
        onChange={(event) => {
          props.onChange(event.target.value === '' ? undefined : event.target.value);
        }}
      />
    );
  }
  if (props.valueType === 'ENUM') {
    const options = readEnumOptions(props.validation);
    return (
      <select
        id={props.id}
        value={props.value}
        disabled={props.disabled}
        aria-required={props.required || undefined}
        onChange={(event) => {
          props.onChange(event.target.value === '' ? undefined : event.target.value);
        }}
      >
        <option value="">—</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    );
  }
  return (
    <input
      id={props.id}
      type="text"
      value={props.value}
      disabled={props.disabled}
      aria-required={props.required || undefined}
      onChange={(event) => {
        props.onChange(event.target.value);
      }}
    />
  );
}

function formatDisplayValue(value: JsonValue | undefined): string {
  if (value === undefined || value === null) {
    return '';
  }
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return '';
}
